/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

@customElement('lead-drawer')
export class LeadDrawer extends LitElement {
  @property({ type: Object }) place: any = null;
  @property({ type: Boolean }) isOpen: boolean = false;
  @state() activeTab: 'overview' | 'photos' | 'reviews' | 'whatsapp' = 'overview';
  @state() activeWhatsAppSubTab: 'profile' | 'status' | 'messages' = 'profile';
  @state() isLoadingWhatsApp: boolean = false;

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('enrich-whatsapp-complete', this.handleWhatsAppComplete);
    window.addEventListener('enrich-whatsapp-error', this.handleWhatsAppError);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('enrich-whatsapp-complete', this.handleWhatsAppComplete);
    window.removeEventListener('enrich-whatsapp-error', this.handleWhatsAppError);
  }

  private handleWhatsAppComplete = (e: any) => {
    if (this.place && e.detail.place_id === this.place.place_id) {
       this.isLoadingWhatsApp = false;
    }
  };

  private handleWhatsAppError = (e: any) => {
    if (this.place && e.detail.place_id === this.place.place_id) {
       alert('Erro ao buscar WhatsApp:\n' + e.detail.error);
    }
  };

  createRenderRoot() {
    return this;
  }

  private close() {
    this.dispatchEvent(new CustomEvent('close-drawer'));
  }

  render() {
    if (!this.place) return html``;
    const p = this.place;
    const isBasic = !p.formatted_phone_number && !p.website && !p.opening_hours;

    return html`
      <div class="drawer-header">
        <h2 style="font-size: 17px; margin: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;" title=${p.name}>${p.name}</h2>
        <button class="drawer-close" style="background:none; border:none; font-size:24px; cursor:pointer;" @click=${this.close}>&times;</button>
      </div>
      
      <div class="drawer-tabs">
        <button class="drawer-tab ${this.activeTab === 'overview' ? 'active' : ''}" @click=${() => this.activeTab = 'overview'}>Visão Geral</button>
        <button class="drawer-tab ${this.activeTab === 'photos' ? 'active' : ''}" @click=${() => this.activeTab = 'photos'}>Fotos (${p.photos ? p.photos.length : 0})</button>
        <button class="drawer-tab ${this.activeTab === 'reviews' ? 'active' : ''}" @click=${() => this.activeTab = 'reviews'}>Avaliações</button>
        <button class="drawer-tab ${this.activeTab === 'whatsapp' ? 'active' : ''}" @click=${() => this.activeTab = 'whatsapp'}>WhatsApp</button>
      </div>

        <div class="drawer-content" style="flex: 1; overflow-y: auto; display: flex; flex-direction: column;">
          ${isBasic ? html`
            <div style="padding: 30px; text-align: center; color: #666; flex: 1; display: flex; flex-direction: column; justify-content: center;">
              <div class="rotating" style="display: inline-block; margin-bottom: 15px; font-size: 24px;">⏳</div>
              <p style="font-size: 14px;">Buscando detalhes do Google...<br>Telefone, site e fotos em breve.</p>
            </div>
          ` : html`
            <div style="flex: 1; overflow-y: auto;">
              ${this.activeTab === 'overview' ? this.renderOverview() : ''}
              ${this.activeTab === 'photos' ? this.renderPhotos() : ''}
              ${this.activeTab === 'reviews' ? this.renderReviews() : ''}
              ${this.activeTab === 'whatsapp' ? this.renderWhatsApp() : ''}
            </div>
          `}
        </div>
    `;
  }

  renderWhatsApp() {
    const p = this.place;
    const w = p.whatsappData;

    if (!p.formatted_phone_number) {
      return html`
        <div style="padding: 40px 20px; text-align: center; color: #666;">
          <div style="font-size: 32px; margin-bottom: 15px;">🚫</div>
          <p>Nenhum telefone encontrado para este lead.</p>
        </div>
      `;
    }

    return html`
      <div class="whatsapp-dashboard-mini" style="display: flex; flex-direction: column; background: #fff;">
        <!-- Submenu Compacto -->
        <div style="display: flex; gap: 15px; padding: 0 15px; border-bottom: 1px solid #eee; background: #fafafa;">
          <button @click=${() => this.activeWhatsAppSubTab = 'profile'} 
            style="padding: 10px 0; border: none; background: none; font-size: 11px; font-weight: 700; color: ${this.activeWhatsAppSubTab === 'profile' ? '#25D366' : '#94a3b8'}; border-bottom: 2px solid ${this.activeWhatsAppSubTab === 'profile' ? '#25D366' : 'transparent'}; cursor: pointer;">
            PERFIL
          </button>
          <button @click=${() => this.activeWhatsAppSubTab = 'status'} 
            style="padding: 10px 0; border: none; background: none; font-size: 11px; font-weight: 700; color: ${this.activeWhatsAppSubTab === 'status' ? '#25D366' : '#94a3b8'}; border-bottom: 2px solid ${this.activeWhatsAppSubTab === 'status' ? '#25D366' : 'transparent'}; cursor: pointer;">
            STATUS
          </button>
          <button @click=${() => this.activeWhatsAppSubTab = 'messages'} 
            style="padding: 10px 0; border: none; background: none; font-size: 11px; font-weight: 700; color: ${this.activeWhatsAppSubTab === 'messages' ? '#25D366' : '#94a3b8'}; border-bottom: 2px solid ${this.activeWhatsAppSubTab === 'messages' ? '#25D366' : 'transparent'}; cursor: pointer;">
            CHAT
          </button>
          <div style="margin-left: auto; align-self: center;">
             <button @click=${() => {
                 if (this.isLoadingWhatsApp) return;
                 this.isLoadingWhatsApp = true;
                 window.dispatchEvent(new CustomEvent('enrich-whatsapp', { detail: p }));
               }} 
                ?disabled=${this.isLoadingWhatsApp}
                style="background: #25D366; color: white; border: none; padding: 6px 14px; border-radius: 8px; font-size: 11px; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 6px; box-shadow: 0 2px 8px rgba(37, 211, 102, 0.3); opacity: ${this.isLoadingWhatsApp ? '0.6' : '1'};">
                ${this.isLoadingWhatsApp ? html`
                  <svg style="animation: spin 1s linear infinite;" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-6.219-8.56"></path></svg>
                  Consultando...
                ` : (w ? 'Atualizar WhatsApp' : 'Verificar WhatsApp')}
             </button>
          </div>
        </div>

         <div style="padding: 20px;">
           ${this.activeWhatsAppSubTab === 'profile' ? html`
              <div style="display: flex; gap: 20px; align-items: flex-start; flex-direction: column;">
                <div style="display: flex; gap: 20px; align-items: center; width: 100%;">
                   <div style="width: 80px; height: 80px; border-radius: 12px; background: #f1f5f9; overflow: hidden; border: 2px solid white; box-shadow: 0 4px 10px rgba(0,0,0,0.05); flex-shrink: 0;">
                      ${w?.picture ? html`<img src="${w.picture}" style="width: 100%; height: 100%; object-fit: cover;" />` : html`<div style="display: flex; align-items: center; justify-content: center; height: 100%; font-size: 32px; color: #cbd5e1;">👤</div>`}
                   </div>
                   <div style="flex: 1;">
                      <div style="font-size: 16px; font-weight: 700; color: #1e293b;">${w?.pushName || 'Consultar perfil...'}</div>
                      <div style="font-size: 13px; color: #64748b; margin-top: 4px;">${p.formatted_phone_number}</div>
                      <div style="display: flex; gap: 8px; flex-wrap: wrap;">
                         ${w?.isBusiness ? html`<span style="display: inline-block; background: #e0f2fe; color: #0369a1; font-size: 10px; padding: 2px 8px; border-radius: 4px; margin-top: 8px; font-weight: 700;">CONTA COMERCIAL</span>` : html`<span style="display: inline-block; background: #f1f5f9; color: #64748b; font-size: 10px; padding: 2px 8px; border-radius: 4px; margin-top: 8px; font-weight: 700;">PESSOAL</span>`}
                         ${w?.category ? html`<span style="display: inline-block; background: #fdf4ff; color: #a21caf; font-size: 10px; padding: 2px 8px; border-radius: 4px; margin-top: 8px; font-weight: 700;">${w.category}</span>` : ''}
                      </div>
                   </div>
                </div>

                ${w?.description ? html`
                  <div style="background: #f8fafc; padding: 15px; border-radius: 10px; border: 1px solid #f1f5f9; width: 100%; margin-top: 10px;">
                     <div style="font-size: 10px; font-weight: 800; color: #94a3b8; margin-bottom: 6px; text-transform: uppercase;">Descrição da Empresa</div>
                     <div style="font-size: 13px; color: #334155; line-height: 1.5; white-space: pre-wrap;">${w.description}</div>
                  </div>
                ` : ''}
                
                ${w?.businessWebsite ? html`
                  <div style="background: #f8fafc; padding: 10px 15px; border-radius: 10px; border: 1px solid #f1f5f9; width: 100%; margin-top: 5px;">
                     <div style="font-size: 10px; font-weight: 800; color: #94a3b8; margin-bottom: 4px; text-transform: uppercase;">Site no WhatsApp</div>
                     <a href="${w.businessWebsite.startsWith('http') ? w.businessWebsite : `https://${w.businessWebsite}`}" target="_blank" style="font-size: 13px; color: #25D366; text-decoration: none; font-weight: 600; word-break: break-all;">${w.businessWebsite}</a>
                  </div>
                ` : ''}
              </div>
           ` : ''}

           ${this.activeWhatsAppSubTab === 'status' ? html`
              <div>
                 <label style="font-size: 10px; font-weight: 800; color: #94a3b8; display: block; margin-bottom: 8px; text-transform: uppercase;">Recado / Biografia</label>
                 <div style="background: #f8fafc; padding: 15px; border-radius: 10px; font-size: 14px; font-style: italic; color: #475569; border: 1px solid #f1f5f9; line-height: 1.5;">
                    ${w?.status ? html`"${w.status}"` : 'Os dados da biografia aparecerão aqui após a consulta à Evolution API.'}
                 </div>
              </div>
           ` : ''}

           ${this.activeWhatsAppSubTab === 'messages' ? html`
              <div style="display: flex; flex-direction: column; gap: 10px;">
                 <a href="https://wa.me/${p.formatted_phone_number.replace(/\D/g, '')}" target="_blank" 
                    style="display: block; text-align: center; background: #25D366; color: white; text-decoration: none; padding: 12px; border-radius: 10px; font-weight: 700; font-size: 14px; box-shadow: 0 4px 10px rgba(37, 211, 102, 0.2);">
                    Abrir Chat no WhatsApp
                 </a>
                 <div style="text-align: center; font-size: 11px; color: #94a3b8; padding-top: 10px;">
                    History e Mensagens em breve.
                 </div>
              </div>
           ` : ''}
        </div>
      </div>
    `;
  }

  renderOverview() {
    const p = this.place;
    if (p.fallback) {
      return html`<div class="info-section"><p style="font-size: 13px; margin: 0;">📍 ${p.formatted_address || ''}</p></div>`;
    }

    const types = p.types 
        ? p.types.map((t: string) => t.replace(/_/g, ' ')).map((t: string) => t.charAt(0).toUpperCase() + t.slice(1)).join(', ') 
        : '';
        
    return html`
      <div class="info-section">
        ${types ? html`<p style="font-size: 13px; color: #666; margin: 0; text-transform: capitalize; line-height: 1.4;">${types}</p>` : ''}
        ${p.rating ? html`<p style="margin: 0; font-size: 15px; margin-top: 4px;"><strong>⭐ ${p.rating}</strong> (${p.user_ratings_total || 0} avaliações)</p>` : ''}
        
        ${p.editorial_summary?.overview ? html`
          <div style="background: var(--color-bg2); padding: 16px; border-radius: 8px; margin-top: 10px; border-left: 4px solid #1a73e8;">
            <p style="font-size: 14px; margin: 0; line-height: 1.5; font-style: italic;">"${p.editorial_summary.overview}"</p>
          </div>
        ` : ''}

        <div style="background: var(--color-bg3); padding: 16px; border-radius: 8px; margin-top: 12px; display: flex; flex-direction: column; gap: 12px;">
          ${p.formatted_address ? html`<p style="margin: 0; font-size: 14px; display: flex; gap: 10px;"><span>📍</span> <span>${p.formatted_address}</span></p>` : ''}
          ${p.formatted_phone_number ? html`<p style="margin: 0; font-size: 14px; display: flex; gap: 10px;"><span>📞</span> <a href="tel:${p.formatted_phone_number}" style="color: #1a73e8; text-decoration: none; font-weight: 500;">${p.formatted_phone_number}</a></p>` : ''}
          ${p.website ? html`<p style="margin: 0; font-size: 14px; display: flex; gap: 10px;"><span>🌐</span> <a href="${p.website}" target="_blank" style="color: #1a73e8; text-decoration: none; font-weight: 500;">Website</a></p>` : ''}
        </div>

        ${p.opening_hours?.weekday_text ? html`
          <div style="margin-top: 16px; background: var(--color-bg3); padding: 16px; border-radius: 8px;">
            <p style="margin: 0 0 10px 0; font-size: 14px; font-weight: 600;">🕒 Horários</p>
            <ul style="list-style: none; padding: 0; margin: 0;">
              ${p.opening_hours.weekday_text.map((t: string) => html`<li style="font-size: 13px; margin-bottom: 4px;">${t}</li>`)}
            </ul>
          </div>
        ` : ''}

        ${p.url ? html`
          <div style="margin-top: 24px; text-align: center; margin-bottom: 20px;">
            <a href="${p.url}" target="_blank" style="display: inline-block; background: #1a73e8; color: white; padding: 14px 28px; border-radius: 24px; font-size: 15px; font-weight: 600; text-decoration: none;">Abrir no Maps</a>
          </div>
        ` : ''}
      </div>
    `;
  }

  renderPhotos() {
    const p = this.place;
    if (!p.photos || p.photos.length === 0) return html`<p style="text-align: center; margin-top: 30px;">Sem fotos.</p>`;
    return html`
      <div class="photo-album">
        ${p.photos.map((photo: any) => {
          const thumbUrl = typeof photo.getUrl === 'function' ? photo.getUrl({ maxWidth: 400 }) : (photo.url || '');
          const fullUrl = typeof photo.getUrl === 'function' ? photo.getUrl({ maxWidth: 1200 }) : (photo.url || '');
          
          if (!thumbUrl) return '';

          return html`
            <a href="${fullUrl}" target="_blank">
              <img src="${thumbUrl}" class="photo-item" loading="lazy" />
            </a>
          `;
        })}
      </div>
    `;
  }

  renderReviews() {
    const p = this.place;
    if (!p.reviews || p.reviews.length === 0) return html`<p style="text-align: center; margin-top: 30px;">Sem avaliações.</p>`;
    return html`
      <div class="reviews-list" style="display: flex; flex-direction: column; gap: 16px;">
        ${p.reviews.map((r: any) => html`
          <div style="background: var(--color-bg2); padding: 16px; border-radius: 12px; border: 1px solid var(--color-sidebar-border);">
            <div style="display: flex; align-items: flex-start; margin-bottom: 12px;">
              <strong style="font-size: 15px; flex: 1;">${r.author_name}</strong>
              <span style="font-size: 14px; font-weight: bold;">⭐ ${r.rating}</span>
            </div>
            <p style="font-size: 14px; margin: 0; line-height: 1.6;">"${r.text}"</p>
          </div>
        `)}
      </div>
    `;
  }
}
